import { CachedChannelApi } from '../../api/CachedChannelApi';
import { EpgUtil } from '../utility/EpgUtil';
import { EpgApi } from '../../api/EpgApi';
import { ActionMethod, AssetType, CardSize, CardType, CmsProperty, SortDir } from '../../api/models/Shared.types';
import {
  ICustomCmsRibbon,
  IRibbon2Container,
  IRibbon2Detail,
  ISchedule,
  IScheduleSummary,
} from '../../api/models/AndromeTV.types';
import { ListRibbon2Asset } from './ListRibbonDataSource';
import { CardDataSource } from '../cms/CardDataSource';
import { CardSizeInfo } from '../cms/CardSizeInfo';
import { ChannelModel } from '../../api/EpgApi.types';
import { Backend } from '../../api/Backend';

export class MiniEpgDataSource {
  private initialized = false;
  private allChannels: ChannelModel[] = [];
  private validChannels: ChannelModel[] = [];
  private backend: Backend;
  private initPromise: Promise<any> | undefined;

  constructor(backend: Backend) {
    this.backend = backend;
  }

  /**
   * Load valid channels
   */
  initialize(): Promise<ChannelModel[]> {
    if (this.initPromise) {
      return this.initPromise;
    } else {
      this.initPromise = new Promise(resolve => {
        if (this.initialized) {
          resolve(this.validChannels);
        } else {
          resolve(
            (this.backend.cachedChannelApi as CachedChannelApi).validChannels(this.backend, false).then(channels => {
              channels.validChannels.forEach(chan => {
                chan.findChannelNumber(this.backend);
              });

              function compare(a: ChannelModel, b: ChannelModel) {
                if (!a.channelNumber || !b.channelNumber) return 0;
                if (a.channelNumber < b.channelNumber) {
                  return -1;
                }
                if (a.channelNumber > b.channelNumber) {
                  return 1;
                }
                return 0;
              }

              channels.validChannels.sort(compare);

              const favPromisses = channels.validChannels.map(el => el.updateFavorite(this.backend));
              return Promise.all(favPromisses).then(() => {
                const favs = channels.validChannels.filter(el => el.favorite);
                const other = channels.validChannels.filter(el => !el.favorite);
                this.validChannels = favs.concat(other);
                this.allChannels = channels.allChannels;
                this.initialized = true;
                return this.validChannels;
              });
            })
          );
        }
      });
    }
    return this.initPromise;
  }

  loadCardSchedules(channelId: string) {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const from = EpgUtil.fromUntilFullDay(yesterday).from;
    const until = EpgUtil.fromUntilFullDay(tomorrow).until;
    return EpgApi.scheduleForChannel(this.backend, channelId, from, until).then(r => {
      // console.log('schedule', r);
      EpgUtil.sortSchedulesByStartDate(r.schedules, SortDir.Asc);
      const assets = r.schedules
        ?.map(schedule => this.scheduleToRibbon2Asset(schedule, r, channelId))
        .filter(schedule => schedule) as ListRibbon2Asset[];
      const cardSzi = new CardSizeInfo();
      const ribboonCont: IRibbon2Container = {
        type: CardType.Landscape,
        items: assets,
      };
      const cmsProps = [
        CmsProperty.ShowScheduleInfo,
        CmsProperty.ShowProgress,
        CmsProperty.UseBestSchedule,
        CmsProperty.HideMiniDetail,
      ];
      const ribbonDat: ICustomCmsRibbon = { cardSize: CardSize.MiniEpg, url: '' };
      cardSzi.updateWithContainer(ribboonCont, cmsProps, ribbonDat);
      const cardDses = assets?.map(el => new CardDataSource(this.backend, el, cardSzi, cmsProps, this.validChannels));
      // add cardDses into a cache maybe
      return cardDses;
    });
  }

  scheduleToRibbon2Asset(
    schedule: IScheduleSummary,
    scheduleForChannel: ISchedule,
    channelId: string
  ): ListRibbon2Asset | undefined {
    const program = scheduleForChannel.programs?.find(p => p.id === schedule.program);

    if (!program) {
      return undefined;
    }

    const resources: IRibbon2Detail[] = [];
    scheduleForChannel.programs?.forEach(program =>
      resources.push(EpgUtil.programToRibbon2Detail(program, scheduleForChannel.seasons))
    );
    // console.log('ListRibbon', this, schedule, program, scheduleForChannel);
    const season = scheduleForChannel.seasons?.find(obj => {
      return obj.id === program.season;
    });
    return {
      asset: {
        ...program,
        title: program.title,
        synopsis: program.shortSynopsis,
        image: program.posterImage!,
        genres: program.genres,
        episode: program.episode,
        season: season?.season,
        published: schedule.published,
        type: AssetType.SCHEDULE,
        id: schedule.id,
      },
      method: ActionMethod.DETAIL,
      channels: [
        {
          id: channelId,
          schedules: [
            {
              ...schedule,
              programId: schedule.program,
            },
          ],
        },
      ],
      resources,
    };
  }
}
